// @ts-strict-ignore
import * as React from 'react';
import { AutomationCategory, IntegrationCapability } from '../../types';
import Button from 'antd/lib/button';
import Divider from 'antd/lib/divider';
import Space from 'antd/lib/space';
import Text from 'antd/lib/typography/Text';
import { displayNameForCapability } from '../../helpers';
import { antGrey, colorWithAlpha, runwayColors, rwColors } from '../styles/globalStyles';
import { displayTextForCategory } from '../../helpers/displayHelpers';
import { iconComponentForCapability, iconComponentForCategory } from '../../components/icons/iconHelpers';

export interface AutomationsToolboxFilterProps {
    availableCategories: AutomationCategory[];
    availableCapabilities: IntegrationCapability[];
    onFilterChange: (categoryFilters: AutomationCategory[], capabilityFilters: IntegrationCapability[]) => void;
    updatedSelectedCategoryFilters: AutomationCategory[];
    updatedSelectedCapabilityFilters: IntegrationCapability[];
}

// updatedSelectedCategoryFilters/updatedSelectedCapabilityFilters allow you to pass in an updated set of selected filters from the parent component, helpful for example if you have a "clear" button on the parent and you want to reset the filters, in that case send empty arrays.
const AutomationsToolboxFilterComponent: React.FC<AutomationsToolboxFilterProps> = ({
    availableCategories,
    availableCapabilities,
    onFilterChange,
    updatedSelectedCategoryFilters,
    updatedSelectedCapabilityFilters,
}) => {
    const [categoryFilters, setCategoryFilters] = React.useState<AutomationCategory[]>([]);
    const [capabilityFilters, setCapabilityFilters] = React.useState<IntegrationCapability[]>([]);

    React.useEffect(() => {
        if (updatedSelectedCategoryFilters) {
            setCategoryFilters(updatedSelectedCategoryFilters);
        }
        if (updatedSelectedCapabilityFilters) {
            setCapabilityFilters(updatedSelectedCapabilityFilters);
        }
        onFilterChange(updatedSelectedCategoryFilters, updatedSelectedCapabilityFilters);
    }, [updatedSelectedCategoryFilters, updatedSelectedCapabilityFilters]);

    const onSelectFilter = (filter) => {
        const newCategories = new Set(categoryFilters);
        const newCapabilities = new Set(capabilityFilters);
        if (availableCategories.includes(filter)) {
            if (newCategories.has(filter)) {
                newCategories.delete(filter);
            } else {
                newCategories.add(filter);
            }
            setCategoryFilters([...newCategories]);
        } else if (availableCapabilities.includes(filter)) {
            if (newCapabilities.has(filter)) {
                newCapabilities.delete(filter);
            } else {
                newCapabilities.add(filter);
            }
            setCapabilityFilters([...newCapabilities]);
        }
        onFilterChange([...newCategories], [...newCapabilities]);
    };

    return availableCategories && availableCapabilities ?
            <Space
                style={{ width: 284 }}
                size={20}
                direction="vertical"
                split={<Divider style={{ backgroundColor: antGrey.grey5 }} />}
            >
                <AutomationsFilterGroup
                    onSelectFilter={onSelectFilter}
                    categoryFilters={categoryFilters}
                    capabilityFilters={capabilityFilters}
                    availableCategories={availableCategories}
                    availableCapabilities={null}
                />
                <AutomationsFilterGroup
                    onSelectFilter={onSelectFilter}
                    categoryFilters={categoryFilters}
                    capabilityFilters={capabilityFilters}
                    availableCategories={null}
                    availableCapabilities={availableCapabilities}
                />
            </Space>
        :   null;
};

export default AutomationsToolboxFilterComponent;

// Only pass either a category or capability array, not both
interface AutomationsFilterGroupProps {
    onSelectFilter: (filter) => void;
    categoryFilters: AutomationCategory[];
    capabilityFilters: IntegrationCapability[];
    availableCategories?: AutomationCategory[];
    availableCapabilities?: IntegrationCapability[];
}
const AutomationsFilterGroup: React.FC<AutomationsFilterGroupProps> = ({
    onSelectFilter,
    categoryFilters,
    capabilityFilters,
    availableCategories,
    availableCapabilities,
}) => {
    const filterTextHeadingStyle = {
        fontSize: 14,
        fontWeight: 400,
        color: antGrey.grey7,
        letterSpacing: '0.1em',
        fontFamily: 'Inter',
    };
    const items = availableCategories ?? availableCapabilities;
    const isCategories = items === availableCategories;
    const titleText = isCategories ? 'CATEGORIES' : 'INTEGRATIONS';

    return (
        <>
            <Space size={22} direction="vertical">
                <Space size={0} direction="vertical">
                    <Text style={{ ...filterTextHeadingStyle }}>FILTER BY</Text>
                    <Text style={{ ...filterTextHeadingStyle, fontWeight: 600, color: antGrey.grey8 }}>
                        {titleText}
                    </Text>
                </Space>
                <Space size={10} direction="vertical">
                    {items.length > 0 &&
                        items.map((item) => {
                            const displayName =
                                isCategories ?
                                    displayTextForCategory(item as AutomationCategory)
                                :   displayNameForCapability(item as IntegrationCapability);
                            const icon =
                                isCategories ?
                                    iconComponentForCategory(item as AutomationCategory)
                                :   iconComponentForCapability(item as IntegrationCapability);
                            return (
                                <Button
                                    key={item}
                                    icon={icon}
                                    style={filterButtonStyle(item, categoryFilters, capabilityFilters)}
                                    onClick={() => onSelectFilter(item)}
                                >
                                    {displayName}
                                </Button>
                            );
                        })}
                </Space>
            </Space>
        </>
    );
};

const filterButtonStyle = (
    filter: IntegrationCapability | AutomationCategory,
    categoryFilters: AutomationCategory[],
    capabilityFilters: IntegrationCapability[],
) => {
    const baseStyle = {
        borderStyle: 'solid',
        borderWidth: 1,
        borderRadius: 100,
        height: 34,
        fontWeight: 400,
        fontSize: 14,
        fontFamily: 'Inter',
    };
    if (
        categoryFilters.includes(filter as AutomationCategory) ||
        capabilityFilters.includes(filter as IntegrationCapability)
    ) {
        return {
            borderColor: runwayColors.primary,
            backgroundColor: runwayColors.primary,
            color: 'white',
            ...baseStyle,
        };
    } else {
        return {
            borderColor: colorWithAlpha(rwColors.primary, 0.8),
            backgroundColor: colorWithAlpha(rwColors.primary, 0.08),
            color: runwayColors.primary,
            ...baseStyle,
        };
    }
};
